import axios, { AxiosError } from 'axios';
import { apiRoute } from '../constants/api-constants';
import { StaysApiError } from './stays-client';
import type { AlertTemplateKind } from '../utils/stays-template-placeholders';

/**
 * LOBBERO STAYS — a listing's alerts and the reservations screen's operations
 * tools (plan S8; lobbero_be/src/stays/routes/{alerts,operations}.js). The
 * DEFAULT axios instance like the other Stays clients (cookies, CSRF,
 * refresh). Every failure is a StaysApiError with the server's code and words.
 */

export interface AlertSettings {
  listing_id: string;
  building_id: number;
  summary_enabled: boolean;
  summary_slots: string[];
  summary_greeting: string;
  show_current_stay: boolean;
  show_arrivals: boolean;
  show_departures: boolean;
  show_next_reservation: boolean;
  show_extras_due: boolean;
  show_cleaning_today: boolean;
  show_turnaround: boolean;
  show_guest_name: boolean;
  show_source: boolean;
  show_price: boolean;
  show_nights: boolean;
  show_arrival_time: boolean;
  show_checkout_time: boolean;
  show_daily_totals: boolean;
  show_quiet_day_note: boolean;
  auto_cleaning_alerts: boolean;
  notify_scheduled_cleaning: boolean;
  notify_unscheduled_cleaning: boolean;
  notify_same_day_turnaround: boolean;
  notify_guest_check_in: boolean;
  notify_new_reservations: boolean;
  notify_cancellations: boolean;
  template_guest_arrived: string | null;
  template_cleaning_scheduled: string | null;
  template_cleaning_unscheduled: string | null;
  template_turnaround: string | null;
  version: number;
  updated_at: string | null;
}

export type AlertBooleanKey = {
  [K in keyof AlertSettings]: AlertSettings[K] extends boolean ? K : never;
}[keyof AlertSettings];

export type AlertDispatchStatus = 'pending' | 'sent' | 'failed' | 'skipped';

export interface AlertDispatchRow {
  kind: string;
  channel: string;
  local_date: string;
  slot: string;
  trigger: string;
  status: AlertDispatchStatus;
  skip_reason: string | null;
  last_error_code: string | null;
  attempts: number;
  sent_at: string | null;
  created_at: string;
}

export interface AlertSettingsState {
  settings: AlertSettings;
  effective_templates: Record<AlertTemplateKind, string>;
  default_templates: Record<AlertTemplateKind, string>;
  is_default: Record<AlertTemplateKind, boolean>;
  placeholders: Record<AlertTemplateKind, string[]>;
  labels: Record<AlertTemplateKind, string>;
  descriptions: Record<AlertTemplateKind, string>;
  building: { timezone: string; zone_note: string };
  recent: AlertDispatchRow[];
}

export interface AlertPreview {
  title: string;
  body: string;
  body_length: number;
  bell_truncated: boolean;
  sample: boolean;
}

export type CleaningStatus = 'unscheduled' | 'scheduled' | 'completed';
export type CleaningAction = 'schedule' | 'complete' | 'reopen' | 'unassign';

export interface CleaningTodayItem {
  reservation_id: string;
  first_name: string;
  check_in: string;
  check_out: string;
  cleaning_status: CleaningStatus;
  cleaning_type: 'agency' | 'self' | null;
  cleaning_fee_cents: number | null;
  currency: string;
  role: 'departing' | 'arriving';
  version: number;
}

export interface TurnaroundPair {
  departing_id: string;
  arriving_id: string;
  date: string;
  out_time: string;
  in_time: string;
  window_minutes: number;
  overlap: boolean;
  urgent: boolean;
  hours_label: string;
  cleaning_booked: boolean;
}

export interface CancelledTodayItem {
  reservation_id: string;
  first_name: string;
  last_initial: string;
  source: string;
  check_in: string;
  check_out: string;
  cancelled_at: string;
}

export interface ExtrasDue {
  available: boolean;
  count?: number;
  items?: { reservation_id: string; amount_cents: number; currency: string; label: string }[];
}

export interface OperationsToday {
  building_today: string;
  timezone: string;
  cleaning_today: CleaningTodayItem[];
  turnarounds: TurnaroundPair[];
  cancelled_today: { count: number; items: CancelledTodayItem[] };
  extras_due: ExtrasDue;
}

export interface CleaningResult {
  cleaning_status: CleaningStatus;
  cleaning_type: 'agency' | 'self' | null;
  cleaning_fee_cents: number | null;
  version: number;
}

const call = async <T>(p: Promise<{ data: T }>, fallback: string): Promise<T> => {
  try {
    const { data } = await p;

    return data;
  } catch (e) {
    const err = e as AxiosError<Record<string, unknown>>;
    if (err && err.response) throw new StaysApiError(err.response.status, err.response.data || null, fallback);
    throw new StaysApiError(0, null, fallback);
  }
};

const listing = (id: string) => `${apiRoute}/stays/listings/${encodeURIComponent(id)}`;

export const getAlertSettings = (listingId: string): Promise<AlertSettingsState> =>
  call(axios.get(`${listing(listingId)}/alert-settings`), "Couldn't load alert settings.");

/** Partial: only the keys sent change. `null` on a template means the default. */
export const saveAlertSettings = (
  listingId: string,
  patch: Partial<Omit<AlertSettings, 'listing_id' | 'building_id' | 'updated_at'>> & { version: number }
): Promise<{ settings: AlertSettings; version: number }> =>
  call(axios.put(`${listing(listingId)}/alert-settings`, patch), 'Could not save the alert settings.');

export const previewAlert = (listingId: string, kind: AlertTemplateKind, template?: string | null): Promise<AlertPreview> =>
  call(
    axios.post(`${listing(listingId)}/alert-settings/preview`, template == null ? { kind } : { kind, template }),
    'Could not show the preview.'
  );

export const sendAlertTest = (
  listingId: string,
  kind: AlertTemplateKind,
  template?: string | null
): Promise<{ sent: boolean; written: number }> =>
  call(
    axios.post(`${apiRoute}/stays/alerts/test`, template == null ? { listing_id: listingId, kind } : { listing_id: listingId, kind, template }),
    'The test could not be sent. Please try again.'
  );

export const sendSummaryNow = (
  listingId: string
): Promise<{ status: 'sent' | 'skipped'; skip_reason?: string; recipients: number }> =>
  call(axios.post(`${apiRoute}/stays/alerts/summary-now`, { listing_id: listingId }), 'The summary could not be sent.');

export const listAlertDispatch = (
  listingId: string,
  before?: string | null,
  limit = 20
): Promise<{ items: AlertDispatchRow[]; next_before: string | null }> =>
  call(
    axios.get(`${listing(listingId)}/alert-dispatch`, { params: before ? { limit, before } : { limit } }),
    "Couldn't load recent alerts."
  );

export const getOperationsToday = (listingId: string): Promise<OperationsToday> =>
  call(axios.get(`${listing(listingId)}/operations-today`), "Couldn't load today's operations.");

export const changeCleaning = (
  reservationId: string,
  input: { action: CleaningAction; version: number; fee_cents?: number | null; by?: 'agency' | 'self' | null }
): Promise<CleaningResult> =>
  call(
    axios.post(`${apiRoute}/stays/reservations/${encodeURIComponent(reservationId)}/cleaning`, input),
    'Could not change the cleaning.'
  );
